"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Bell,
  Bot,
  Boxes,
  Cpu,
  Database,
  FileText,
  Globe,
  Layers,
  LineChart,
  Network,
  Rocket,
  Search,
  Shield,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Zap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import heroImage from "@/public/images/dayttagangwork.png";
import realtimeImage from "@/public/images/realtime.jpg";
import workflowImage from "@/public/images/workflow.jpg";

// External link used for "Request access" / "Talk to the founder" CTAs.
const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

// Sophisticated animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

// Floating icon configuration - refined and subtle
const floatingIcons = [
  { Icon: LineChart, position: "top-24 left-[8%]", delay: 0, size: "w-4 h-4" },
  { Icon: Shield, position: "top-32 right-[12%]", delay: 1.5, size: "w-4 h-4" },
  {
    Icon: Network,
    position: "bottom-40 left-[12%]",
    delay: 3,
    size: "w-4 h-4",
  },
  {
    Icon: Zap,
    position: "top-1/3 right-[8%]",
    delay: 0.8,
    size: "w-3.5 h-3.5",
  },
  {
    Icon: Bot,
    position: "bottom-1/3 right-[18%]",
    delay: 2.2,
    size: "w-3.5 h-3.5",
  },
  { Icon: FileText, position: "top-1/2 left-[6%]", delay: 4, size: "w-4 h-4" },
];

// Prompt examples shown as chips under the hero CTA.
const heroPrompts = [
  "Extract every debt covenant from this 10-K.",
  "Build a DCF with bear, base, and bull cases.",
  "Alert me when volume and volatility break trend.",
  "Brief me on what changed since the last filing.",
];

// Hero Section - Refined and Elegant
const HeroSection = () => {
  const prefersReducedMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const parallaxY = useTransform(
    scrollY,
    [0, 600],
    [0, prefersReducedMotion ? 0 : 80],
  );

  return (
    <section
      aria-labelledby="home-hero-title"
      id="home-hero"
      className="relative w-full overflow-hidden min-h-[92vh] flex items-center justify-center"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pattern-grid opacity-30" />

      {/* Ambient glow */}
      <div className="absolute inset-0 ambient-glow" />

      {/* Background Image with Parallax */}
      <motion.div
        className="absolute inset-0 z-0 opacity-20 dark:opacity-10"
        style={{ y: parallaxY }}
      >
        <Image
          src={heroImage}
          alt="Financial professionals building tools with Kautious"
          className="object-cover object-center w-full h-full grayscale"
          fill
          priority
          placeholder="blur"
          sizes="100vw"
          quality={90}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-transparent to-background" />
      </motion.div>

      {/* Refined Floating Icons - subtle and elegant */}
      {!prefersReducedMotion && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden lg:block z-10">
          {floatingIcons.map(({ Icon, position, delay, size }, index) => (
            <motion.div
              key={index}
              className={`absolute ${position}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: [0.15, 0.25, 0.15],
                y: [0, -8, 0],
              }}
              transition={{
                opacity: {
                  duration: 4,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                  delay,
                },
                y: {
                  duration: 5,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                  delay,
                },
              }}
            >
              <div className="w-10 h-10 rounded-lg bg-foreground/[0.03] dark:bg-foreground/[0.04] border border-foreground/[0.06] dark:border-foreground/[0.08] flex items-center justify-center backdrop-blur-sm">
                <Icon
                  className={`${size} text-foreground/20 dark:text-foreground/25`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Hero Content */}
      <div className="relative z-20 section-container">
        <div className="flex flex-col items-center text-center max-w-5xl mx-auto px-4 py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <span className="badge-professional inline-flex items-center px-4 py-2 rounded-full text-sm tracking-wide">
              <Sparkles className="w-4 h-4 mr-2" />
              Financial intelligence without the research drag
            </span>
          </motion.div>

          <motion.h1
            id="home-hero-title"
            className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <span className="block">Find the signal.</span>
            <span className="block mt-2 text-gradient-gold">
              Before the market does.
            </span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            Kautious turns filings, market data, and plain-English requests into
            source-grounded research, live monitors, and working financial
            tools. Spend less time collecting the facts — and more time deciding
            what they mean.
          </motion.p>

          {/* Prompt example chips */}
          <motion.div
            className="flex flex-wrap justify-center gap-2 mb-12 max-w-3xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            {heroPrompts.map((prompt) => (
              <span
                key={prompt}
                className="text-xs md:text-sm text-muted-foreground/90 border border-border/70 bg-background/50 rounded-full px-3 py-1.5 backdrop-blur-sm"
              >
                {prompt}
              </span>
            ))}
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-4 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            <Button size="xl" variant="premium" asChild>
              <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                Request Desk Access
                <ArrowRight className="w-5 h-5 ml-1" />
              </a>
            </Button>
            <Button size="xl" variant="outline" asChild>
              <Link href="#pillars">
                <Globe className="w-5 h-5 mr-2" />
                See How the Edge Is Built
              </Link>
            </Button>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="flex flex-col items-center gap-4"
          >
            <p className="text-xs text-muted-foreground/70 font-medium tracking-[0.2em] uppercase">
              Hands-on onboarding for financial teams with a live workflow
            </p>
            <div className="h-px w-24 accent-line" />
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />
    </section>
  );
};

// The Enemy + 60-second win
const EnemySection = () => (
  <section className="section-padding relative overflow-hidden">
    <div className="absolute inset-0 bg-secondary/30 dark:bg-secondary/20" />
    <div className="absolute inset-0 pattern-grid opacity-20" />

    <div className="section-container relative z-10">
      <motion.div
        className="max-w-3xl mx-auto text-center mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p
          variants={fadeInUp}
          className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-4"
        >
          The Cost of Slow
        </motion.p>
        <motion.h2 variants={fadeInUp} className="mb-6">
          Your analysts are too valuable to spend their day moving data.
        </motion.h2>
        <motion.div
          variants={fadeInUp}
          className="h-px w-16 mx-auto accent-line mb-6"
        />
        <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
          Manual extraction burns expensive hours. Fixed terminals hide your
          edge behind rigid interfaces. Tool requests age in someone else&apos;s
          backlog. Meanwhile, the filing gets longer and the market keeps
          moving. Kautious turns that drag into a controlled, auditable
          workflow.
        </motion.p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={staggerContainer}
      >
        {[
          {
            step: "01",
            title: "Ask at full speed",
            body: "Describe the filing analysis, model, monitor, report, or workflow you need in plain English.",
          },
          {
            step: "02",
            title: "Inspect the evidence",
            body: "Kautious structures the data, builds a working preview, and keeps the source trail visible.",
          },
          {
            step: "03",
            title: "Put it to work",
            body: "Revise, approve, and deploy a reusable tool your desk can run again without rebuilding the process.",
          },
        ].map((item) => (
          <motion.div
            key={item.step}
            variants={fadeInUp}
            className="surface-elevated rounded-lg p-8"
          >
            <span className="text-3xl font-serif text-[hsl(var(--accent-gold)/0.4)]">
              {item.step}
            </span>
            <h3 className="text-xl mt-4 mb-2">{item.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {item.body}
            </p>
          </motion.div>
        ))}
      </motion.div>

      <motion.p
        className="text-center text-sm text-muted-foreground/80 mt-10 italic"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        Less data wrangling. Faster research cycles. Every conclusion tied back
        to evidence.
      </motion.p>
    </div>
  </section>
);

// Product pillars - what Kautious does
const pillars = [
  {
    icon: Boxes,
    title: "Build",
    description:
      "Create sheets, streams, apps, reports, forecasts, and caseflows from natural language. Preview first, modify in plain English, approve when ready.",
  },
  {
    icon: Search,
    title: "Research",
    description:
      "Work with SEC filings, XBRL, entity extraction, research notes, forecasts, watchlists, positions, and persistent memory in one workspace.",
  },
  {
    icon: Bell,
    title: "Monitor",
    description:
      "Track watchlists, market conditions, price and volume alerts, deployed streams, and workflow outcomes before they become meeting surprises.",
  },
  {
    icon: Bot,
    title: "Agents",
    description:
      "Run analyst, auditor, autonomous, and managed agents against real workflows with visible progress, task history, and human approval gates.",
  },
  {
    icon: Rocket,
    title: "Deploy",
    description:
      "Treat generated tools like software: preview, promote, monitor, roll back, and keep a clean record of what changed and why.",
  },
  {
    icon: ShieldCheck,
    title: "Audit",
    description:
      "Preserve reasoning, tool calls, decisions, permissions, and workspace activity so the work can survive scrutiny.",
  },
];

const PillarsSection = () => (
  <section id="pillars" className="section-padding relative">
    <div className="absolute inset-0 bg-secondary/20 dark:bg-secondary/10" />

    <div className="section-container relative z-10">
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p
          variants={fadeInUp}
          className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-4"
        >
          What Kautious Does
        </motion.p>
        <motion.h2 variants={fadeInUp} className="mb-6">
          From raw filing to defensible decision.
        </motion.h2>
        <motion.div
          variants={fadeInUp}
          className="h-px w-16 mx-auto accent-line"
        />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {pillars.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="surface-elevated rounded-lg p-8 group"
            >
              <div className="w-12 h-12 rounded-lg bg-[hsl(var(--accent-gold)/0.1)] flex items-center justify-center mb-6 transition-colors group-hover:bg-[hsl(var(--accent-gold)/0.15)]">
                <Icon className="w-6 h-6 text-[hsl(var(--accent-gold))]" />
              </div>
              <h3 className="text-lg mb-3">{feature.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// Three products: the app, the data platform, and the orchestration backend.
const products = [
  {
    name: "Kautious",
    layer: "The App",
    tagline: "Turn research drag into decision speed",
    description:
      "The AI research desk for financial professionals. Turn filings, market data, and plain-English requests into source-grounded analysis, live monitors, and reusable tools.",
    href: "/kautious",
    icon: Boxes,
    accentColor: "var(--product-markets)",
    features: [
      "Plain-English builds",
      "Read-only & auditable",
      "Desktop + web",
    ],
  },
  {
    name: "Daytta",
    layer: "The Data Platform",
    tagline: "Raw data into decisions",
    description:
      "The data-heavy analytics platform. Daytta composes the Extract, Graph, and Time engines on the Llamox runtime to turn documents, knowledge, and time-series into governed data products — customizable to any data-heavy domain.",
    href: "/daytta",
    icon: Database,
    accentColor: "var(--product-data)",
    features: [
      "Extract · Graph · Time engines",
      "Customizable pipelines",
      "Financial markets, first",
    ],
  },
  {
    name: "Llamox",
    layer: "The Backend",
    tagline: "Governed AI operations",
    description:
      "The orchestration control plane underneath. Agents build, humans approve, and the platform deploys with sandboxes, audit trails, and rollback.",
    href: "/llamox",
    icon: Cpu,
    accentColor: "var(--product-orchestrate)",
    features: ["Managed agents", "Approval workflows", "Sandboxed execution"],
  },
] as const;

// Daytta's composable engines, also available to advanced teams directly.
const dayttaEngines = [
  {
    name: "Kautious Extract",
    blurb: "Messy documents → defensible, source-grounded data.",
    href: "/extract",
    icon: FileText,
  },
  {
    name: "Kautious Graph",
    blurb: "Temporal memory and knowledge for AI agents.",
    href: "/graph",
    icon: Network,
  },
  {
    name: "Kautious Time",
    blurb: "Backtested time-series forecasting you can defend.",
    href: "/time",
    icon: LineChart,
  },
];

const ProductsSection = () => (
  <section id="products" className="section-padding relative">
    <div className="section-container">
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p
          variants={fadeInUp}
          className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-4"
        >
          Three Products, One Stack
        </motion.p>
        <motion.h2 variants={fadeInUp} className="mb-6">
          The app, the data platform, and the backend.
        </motion.h2>
        <motion.div
          variants={fadeInUp}
          className="h-px w-16 mx-auto accent-line mb-6"
        />
        <motion.p
          variants={fadeInUp}
          className="text-lg text-muted-foreground max-w-2xl mx-auto"
        >
          Kautious is the app financial professionals use. Daytta is the data
          platform behind it. Llamox is the governed runtime they both run on —
          each available on its own to teams that want it.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {products.map((product, index) => {
          const Icon = product.icon;
          const accent = product.accentColor
            ? `hsl(${product.accentColor})`
            : "hsl(var(--accent-gold))";
          const accentSoft = product.accentColor
            ? `hsl(${product.accentColor} / 0.1)`
            : "hsl(var(--accent-gold) / 0.1)";
          return (
            <motion.div
              key={product.name}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link
                href={product.href}
                className="group block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent-gold))] focus-visible:ring-offset-4 rounded-lg"
                aria-label={`Learn more about ${product.name}`}
              >
                <article className="h-full surface-elevated rounded-lg p-8 lg:p-10 transition-all duration-500 group-hover:-translate-y-1 relative overflow-hidden">
                  {/* Accent line at top */}
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px]"
                    style={{ backgroundColor: accent }}
                  />

                  {/* Layer badge */}
                  <p
                    className="text-[10px] font-bold uppercase tracking-[0.2em] mb-6"
                    style={{ color: accent }}
                  >
                    {product.layer}
                  </p>

                  {/* Icon */}
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: accentSoft }}
                  >
                    <Icon className="w-7 h-7" style={{ color: accent }} />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl mb-2">{product.name}</h3>
                  <p
                    className="text-sm font-medium mb-4"
                    style={{ color: accent }}
                  >
                    {product.tagline}
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-8">
                    {product.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {product.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-3 text-sm"
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: accent }}
                        />
                        <span className="text-foreground/80">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <div className="flex items-center text-sm font-medium transition-colors group-hover:text-[hsl(var(--accent-gold))]">
                    <span>Explore {product.name}</span>
                    <ArrowUpRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </article>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Daytta's engines */}
      <motion.div
        className="mt-20 max-w-5xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={staggerContainer}
      >
        <motion.p
          variants={fadeInUp}
          className="text-center text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-3"
        >
          Inside Daytta
        </motion.p>
        <motion.p
          variants={fadeInUp}
          className="text-center text-sm text-muted-foreground max-w-2xl mx-auto mb-10"
        >
          Financial markets is our first data-analytics example. The same
          engines are built to be customized to any data-heavy domain.
        </motion.p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {dayttaEngines.map((engine) => {
            const Icon = engine.icon;
            return (
              <Link
                key={engine.name}
                href={engine.href}
                className="group surface-elevated rounded-lg p-6 transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent-gold))]"
              >
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--product-data)/0.1)] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[hsl(var(--product-data))]" />
                </div>
                <h3 className="text-base mb-1.5">{engine.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {engine.blurb}
                </p>
              </Link>
            );
          })}
        </div>
      </motion.div>
    </div>
  </section>
);

// Product Proof Section
const ProductProofSection = () => (
  <section className="section-padding relative">
    <div className="section-container">
      <motion.div
        className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <div>
          <motion.p
            variants={fadeInUp}
            className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-4"
          >
            Product Proof
          </motion.p>
          <motion.h2 variants={fadeInUp}>
            See the work, not the pitch.
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-muted-foreground mt-4 max-w-xl"
          >
            Interfaces from the live product — working tools, visible evidence,
            and governed handoffs instead of another AI demo.
          </motion.p>
        </div>
        <motion.div variants={fadeInUp}>
          <Link href="/console">
            <Button variant="premium">
              Open Console
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </motion.div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {[
          {
            product: "Kautious",
            title: "Build, preview, deploy",
            description:
              "Describe a screener, monitor, or report and watch a working preview appear. Approve it and it lands on your desk — reusable and shareable.",
            image: realtimeImage,
            badge: "Prompt to tool",
            color: "var(--product-markets)",
          },
          {
            product: "Llamox",
            title: "Approval workflows",
            description:
              "Structured handoffs between agents and humans, with traceable steps and a rollback path for every consequential decision.",
            image: workflowImage,
            badge: "Audit Ready",
            color: "var(--product-orchestrate)",
          },
        ].map((item, index) => (
          <motion.article
            key={item.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="surface-elevated rounded-lg overflow-hidden group"
          >
            <div className="p-6 lg:p-8 flex items-start justify-between gap-4">
              <div>
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2"
                  style={{ color: `hsl(${item.color})` }}
                >
                  {item.product}
                </p>
                <h3 className="text-xl mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {item.description}
                </p>
              </div>
              <Badge
                className="shrink-0 text-[10px] font-semibold tracking-wider"
                style={{
                  backgroundColor: `hsl(${item.color} / 0.1)`,
                  color: `hsl(${item.color})`,
                  borderColor: `hsl(${item.color} / 0.2)`,
                }}
              >
                {item.badge}
              </Badge>
            </div>
            <div className="relative h-64 lg:h-72 overflow-hidden">
              <Image
                src={item.image}
                alt={`${item.title} interface preview`}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-105"
                placeholder="blur"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

// De-risking section
const DeRiskSection = () => (
  <section className="section-padding relative">
    <div className="absolute inset-0 bg-secondary/20 dark:bg-secondary/10" />

    <div className="section-container relative z-10">
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p
          variants={fadeInUp}
          className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-4"
        >
          Speed without the execution risk
        </motion.p>
        <motion.h2 variants={fadeInUp} className="mb-6">
          Analyze everything. Execute nothing.
        </motion.h2>
        <motion.div
          variants={fadeInUp}
          className="h-px w-16 mx-auto accent-line mb-6"
        />
        <motion.p
          variants={fadeInUp}
          className="text-lg text-muted-foreground max-w-2xl mx-auto"
        >
          Kautious can connect to portfolio and market data, but it is not a
          trading platform. There is no trade execution in the product.
          Generated work goes through preview and approval, source evidence
          stays visible, and every consequential action leaves an audit trail.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {[
          {
            icon: Shield,
            title: "Read-only access",
            description:
              "Connect portfolio and market data for analysis. Analyze everything, execute nothing.",
          },
          {
            icon: ShieldCheck,
            title: "Human approval",
            description:
              "High-impact actions stop for human review before anything reaches production.",
          },
          {
            icon: TrendingUp,
            title: "Auditable by default",
            description:
              "Reasoning, tool calls, permissions, and decisions are preserved so work survives scrutiny.",
          },
        ].map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="surface-elevated rounded-lg p-8 text-center"
            >
              <div className="w-12 h-12 rounded-lg bg-[hsl(var(--accent-gold)/0.1)] flex items-center justify-center mb-6 mx-auto">
                <Icon className="w-6 h-6 text-[hsl(var(--accent-gold))]" />
              </div>
              <h3 className="text-lg mb-3">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// Final CTA Section
const CTASection = () => (
  <section className="section-padding relative overflow-hidden">
    <div className="absolute inset-0 cta-gradient" />
    <div className="absolute inset-0 pattern-grid opacity-5" />

    {/* Subtle glow */}
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[hsl(var(--accent-gold)/0.1)] rounded-full blur-[120px]" />

    <div className="section-container text-center relative z-10">
      <motion.div
        className="max-w-3xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.h2
          variants={fadeInUp}
          className="text-4xl md:text-5xl lg:text-6xl text-white mb-8"
        >
          The next material fact is already in the filing.
        </motion.h2>
        <motion.p
          variants={fadeInUp}
          className="text-lg md:text-xl mb-12 text-white/70 leading-relaxed max-w-2xl mx-auto"
        >
          The question is whether your team finds it before the market does.
          Bring us a live research workflow and we&apos;ll show you where
          Kautious removes the drag.
        </motion.p>
        <motion.div
          variants={fadeInUp}
          className="flex flex-col sm:flex-row justify-center gap-4"
        >
          <Button
            size="xl"
            className="bg-white text-slate-900 hover:bg-white/90 font-semibold shadow-xl"
            asChild
          >
            <a href={CONTACT_URL} target="_blank" rel="noreferrer">
              Request Desk Access
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
            size="xl"
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            asChild
          >
            <Link href="/extract">See Kautious Extract</Link>
          </Button>
        </motion.div>
        <motion.div variants={fadeInUp} className="mt-12">
          <div className="h-px w-16 mx-auto bg-gradient-to-r from-transparent via-[hsl(var(--accent-gold)/0.5)] to-transparent mb-6" />
          <p className="text-xs text-white/40 font-medium tracking-[0.15em] uppercase">
            Bring a real workflow
          </p>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Main HomePage Component
export default function HomePage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 w-full z-50 glass-header">
        <nav className="section-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="ghost" size="sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="premium" size="sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        <HeroSection />
        <EnemySection />
        <PillarsSection />
        <ProductsSection />
        <ProductProofSection />
        <DeRiskSection />
        <CTASection />
      </main>

      {/* Footer */}
      <footer className="footer-bg">
        <div className="section-container py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 space-y-4">
              <Logo />
              <p className="text-sm text-muted-foreground leading-relaxed">
                Find the signal before the market does. Source-grounded
                research, live monitors, and auditable financial tools.
              </p>
            </div>
            <div>
              <h4 className="font-sans font-semibold text-sm mb-6 text-foreground tracking-wide">
                Products
              </h4>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {[
                  { label: "Kautious", href: "/kautious" },
                  { label: "Desktop App", href: "/desktop" },
                  { label: "Daytta", href: "/daytta" },
                  { label: "Llamox", href: "/llamox" },
                  { label: "Console", href: "/console" },
                ].map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-sans font-semibold text-sm mb-6 text-foreground tracking-wide">
                Daytta Engines
              </h4>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {[
                  { label: "Kautious Extract", href: "/extract" },
                  { label: "Kautious Graph", href: "/graph" },
                  { label: "Kautious Time", href: "/time" },
                ].map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-sans font-semibold text-sm mb-6 text-foreground tracking-wide">
                Company
              </h4>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {[
                  { label: "Docs", href: "/docs" },
                  { label: "Blog", href: "/blog" },
                  { label: "Investors", href: "/investors" },
                  {
                    label: "Contact",
                    href: CONTACT_URL,
                    external: true,
                  },
                ].map((item) => (
                  <li key={item.label}>
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-foreground transition-colors"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        href={item.href}
                        className="hover:text-foreground transition-colors"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Kautious. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <ModeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
